<script setup lang="ts">
import { contractTypeLabel } from '~/utils/contractTypeLabel'
import { Check, Plus, Search } from 'lucide-vue-next'
import { useWebChatStore } from '~/stores/web-chat'
import type { SearchResultsCard } from '~/types/web-chat'

const props = defineProps<{ card: SearchResultsCard }>()

const { t, te } = useI18n()
const webChat = useWebChatStore()

function inContext(id: string): boolean {
  return webChat.isAttached(id)
}

function toggle(doc: SearchResultsCard['docs'][number]) {
  if (inContext(doc.id)) {
    webChat.detachDocument(doc.id)
  } else {
    webChat.attachDocument({ id: doc.id, name: doc.name, file_name: doc.file_name })
  }
}

function fileExt(name: string | null): string {
  if (!name) return 'DOC'
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : 'DOC'
}

function extColor(ext: string): string {
  if (ext === 'PDF') return 'bg-destructive/15 text-destructive'
  if (ext === 'DOCX') return 'bg-info/15 text-info'
  return 'bg-muted text-muted-foreground'
}
</script>

<template>
  <div class="mt-3 rounded-lg border border-primary/20 bg-primary-lighter p-3">
    <div class="flex items-center gap-2">
      <div class="h-5 w-5 rounded-full bg-primary-light flex items-center justify-center shrink-0">
        <Search class="h-3 w-3 text-primary" />
      </div>
      <p class="text-xs font-medium text-foreground">
        {{ props.card.intro || t('webChat.searchResults.title') }}
        <span class="text-muted-foreground/70 font-normal">({{ props.card.docs.length }})</span>
      </p>
    </div>
    <p class="mt-1 ml-7 text-[11px] text-muted-foreground/80 leading-relaxed">
      {{ t('webChat.searchResults.hint') }}
    </p>

    <ul class="mt-2.5 flex flex-col gap-1.5">
      <li
        v-for="doc in props.card.docs"
        :key="doc.id"
      >
        <button
          type="button"
          class="group flex w-full items-center gap-2 rounded-md border bg-background px-2 py-1.5 text-left transition-colors"
          :class="inContext(doc.id) ? 'border-primary/40 bg-accent-lightest' : 'border-border hover:bg-accent-lightest'"
          @click="toggle(doc)"
        >
          <span
            class="inline-flex items-center justify-center h-5 max-w-[80px] truncate px-1.5 text-[9px] font-bold rounded-md shrink-0"
            :class="doc.contract_type ? 'bg-accent-light text-accent-light-foreground' : extColor(fileExt(doc.file_name))"
          >
            {{ doc.contract_type ? contractTypeLabel(doc.contract_type, { t, te }) : fileExt(doc.file_name) }}
          </span>
          <span class="min-w-0 flex-1">
            <span class="block text-xs font-medium text-foreground truncate">{{ doc.name }}</span>
            <span
              v-if="doc.snippet"
              class="block text-[11px] text-muted-foreground truncate"
            >{{ doc.snippet }}</span>
          </span>
          <span
            class="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium shrink-0"
            :class="inContext(doc.id)
              ? 'text-primary'
              : 'text-muted-foreground group-hover:text-foreground'"
          >
            <Check v-if="inContext(doc.id)" class="h-3.5 w-3.5" />
            <Plus v-else class="h-3.5 w-3.5" />
            {{ inContext(doc.id) ? t('webChat.searchResults.added') : t('webChat.searchResults.add') }}
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>